import { UserSwitchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTaskListsThunk, getWorkGetScheduleThunk } from "tm-shared/api";
import { AnyTask, Market, WorkStatus } from "tm-shared/types";

import { ProgressLoader } from "@/app/ProgressLoader";
import BaseButton from "@/components/BaseButton";
import Header from "@/components/Header";
import { EmptyMarkets } from "@/components/ItemListView/EmptyMarkets";
import { MainContainer } from "@/components/Layout/Styled";
import Meta from "@/components/Meta";
import { TasksModalsContainer } from "@/components/TasksModals/TasksModalsContainer";
import { HeaderNavLink } from "@/components/UI/HeaderNavLink";
import { HeaderReloadBtn } from "@/components/UI/HeaderReloadBtn";
import { UniversalTabs } from "@/components/UniversalTabs";
import { ROUTES } from "@/constants";
import { MANAGER_AGENT_VOCABULARY } from "@/constants/domain/managerAgentVocabulary";
import { isManagerRole } from "@/hooks/isManagerRole";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import useAsyncAction from "@/hooks/useAsyncAction";
import { useAsyncTaskQueue } from "@/hooks/useAsyncTaskQueue";
import { useFilteredMarkets } from "@/hooks/useFilteredMarkets";
import { useGlobalCooldown } from "@/hooks/useGlobalCooldown";
import { useQueueInfo } from "@/hooks/useQueueInfo";
import { DayStartedModal } from "@/pages/WorkPlan/modals/DayStartedModal";
import { setWorkPlanActiveCollapse } from "@/store/slices/ui";

import { MarketList } from "./components/MarketList";
import { SearchAndSortControls } from "./components/SearchAndSortControls";
import { WorkControls } from "./components/WorkControls";
import { SkipPointModal } from "./modals/SkipPointModal";
import { StyledWPMarketsWrapper } from "./Styled";

type WorkPlanTab = "general" | "extra" | "completed";
type ModalType = "skipPoint" | "dayStarted" | null;

const TABS_CONFIG = [
    { label: "Основні", value: "general" as const },
    { label: "Позапланові", value: "extra" as const },
    { label: "Завершені", value: "completed" as const },
] as const;

const EMPTY_MESSAGES = {
    general: "Всі основні точки виконано. Чудова робота!",
    extra: "Позапланових точок поки що немає",
    completed: "Завершених точок поки що немає",
    noActiveRoute:
        "Ви ще не завантажили робочий план жодного співробітника. Перейдіть до розділу команди та оберіть працівника, щоб розпочати роботу з його маршрутом.",
} as const;

const COOLDOWN_DURATION = 30_000;

const WorkPlan = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    // Local state
    const [activeTab, setActiveTab] = useState<WorkPlanTab>("general");
    const [modal, setModal] = useState<ModalType>(null);
    const [tasksInfoModal, setTasksInfoModal] = useState<{
        market: Market;
        task: AnyTask;
    } | null>(null);

    // Store selectors
    const { markets, marketExtraIds, tasks, workStatus } = useAppSelector(
        (s) => s.work,
    );
    const { activeSchedule, lastUpdated } = useAppSelector((s) => s.sv);
    const { id: userId, role } = useAppSelector((s) => s.user);
    const { marketsSortOption, query, activeCollapse } = useAppSelector(
        (s) => s.ui.workPlan,
    );
    const { offlineMode, isUnscheduledMarketsProcessing } = useAppSelector(
        (s) => s.offline,
    );

    // Custom hooks
    const { queue, progress: queueProgress } = useAsyncTaskQueue();
    const { disabled: isCooldownActive, trigger: triggerCooldown } =
        useGlobalCooldown(COOLDOWN_DURATION);
    const { hasPending } = useQueueInfo();

    const {
        filteredMarkets: generalMarkets,
        filteredExtraMarkets: extraMarkets,
        filteredCompletedMarkets: completedMarkets,
    } = useFilteredMarkets(
        markets,
        tasks,
        marketExtraIds,
        query,
        marketsSortOption,
    );

    // Computed values
    const isManager = isManagerRole(role);
    const isWorking = isManager ? true : workStatus === WorkStatus.StartWork;

    const isScheduleEmpty =
        !activeSchedule ||
        !dayjs(lastUpdated.activeSchedule, "DD.MM.YYYY HH:mm:ss").isSame(
            dayjs(),
            "day",
        );
    const isEmptyManagerWorkPlan = isManager && isScheduleEmpty;

    const marketsByTab = useMemo(
        () => ({
            general: generalMarkets,
            extra: extraMarkets,
            completed: completedMarkets,
        }),
        [generalMarkets, extraMarkets, completedMarkets],
    );

    const tabsWithCounts = useMemo(
        () =>
            TABS_CONFIG.map((tab) => ({
                ...tab,
                count: marketsByTab[tab.value]?.length || 0,
            })),
        [marketsByTab],
    );

    const isReloadDisabled =
        isCooldownActive ||
        offlineMode ||
        hasPending ||
        isUnscheduledMarketsProcessing;

    // Handlers
    const [handleSchedulesReload, isReloadLoading] = useAsyncAction(
        async () => {
            triggerCooldown();
            const marketIds = Object.keys(markets);
            const roleSlug = isManager
                ? MANAGER_AGENT_VOCABULARY[role]
                : undefined;

            await Promise.all([
                dispatch(
                    getWorkGetScheduleThunk({
                        userId: isManager ? activeSchedule : userId,
                        roleSlug,
                    }),
                ),
                dispatch(
                    getTaskListsThunk({
                        targetIds: marketIds,
                        roleSlug,
                    }),
                ),
            ]);
        },
    );

    const handleChangeTab = useCallback(
        (tab: string) => {
            dispatch(setWorkPlanActiveCollapse(null));
            setActiveTab(tab as WorkPlanTab);
        },
        [dispatch],
    );

    const handleCloseModal = useCallback(() => {
        setModal(null);
    }, []);

    const handleOpenDayStartedModal = useCallback(() => {
        setModal("dayStarted");
    }, []);

    const handleNavigateToTeam = useCallback(() => {
        navigate(ROUTES.Team);
    }, [navigate]);

    // Effects
    useEffect(() => {
        if (!activeCollapse) return;

        const findTabByMarketId = (): WorkPlanTab | null => {
            if (generalMarkets.some((m) => m.id === activeCollapse))
                return "general";
            if (extraMarkets.some((m) => m.id === activeCollapse))
                return "extra";
            if (completedMarkets.some((m) => m.id === activeCollapse))
                return "completed";
            return null;
        };

        const targetTab = findTabByMarketId();

        if (targetTab && targetTab !== activeTab) {
            setActiveTab(targetTab);
        }
    }, [
        activeCollapse,
        generalMarkets,
        extraMarkets,
        completedMarkets,
        activeTab,
    ]);

    // Render helpers
    const renderHeaderActions = useCallback(
        () => (
            <>
                <HeaderNavLink to={ROUTES.AssignmentsPage} end>
                    <UserSwitchOutlined />
                </HeaderNavLink>
                <HeaderReloadBtn
                    onClick={handleSchedulesReload}
                    disabled={isReloadDisabled}
                />
            </>
        ),
        [handleSchedulesReload, isReloadDisabled],
    );

    const handleOpenTasksInfoModal = ({
        market,
        task,
    }: {
        market: Market;
        task: AnyTask;
    }) => {
        setTasksInfoModal({
            market,
            task,
        });
    };

    const handleCloseTasksInfoModal = () => {
        setTasksInfoModal(null);
    };

    const renderWorkContent = () => {
        const currentMarkets = marketsByTab[activeTab];
        const hasMarkets = currentMarkets?.length > 0;

        return (
            <>
                {isWorking && (
                    <>
                        <UniversalTabs
                            tabs={tabsWithCounts}
                            value={activeTab}
                            onChange={handleChangeTab}
                            maxWidth="400px"
                        />
                        <SearchAndSortControls />
                    </>
                )}

                <StyledWPMarketsWrapper>
                    {isWorking &&
                        (hasMarkets ? (
                            <MarketList
                                markets={currentMarkets}
                                onOpenTasksInfoModal={handleOpenTasksInfoModal}
                            />
                        ) : (
                            <EmptyMarkets text={EMPTY_MESSAGES[activeTab]} />
                        ))}

                    <WorkControls
                        queue={queue}
                        openDayStartedModal={handleOpenDayStartedModal}
                    />
                </StyledWPMarketsWrapper>
            </>
        );
    };

    // Early return for empty manager work plan
    if (isEmptyManagerWorkPlan) {
        return (
            <>
                <Header />
                <MainContainer>
                    <EmptyMarkets text={EMPTY_MESSAGES.noActiveRoute} />
                    <BaseButton type="primary" onClick={handleNavigateToTeam}>
                        Відкрити список команди
                    </BaseButton>
                </MainContainer>
            </>
        );
    }

    return (
        <>
            <Meta title="Робочий план" />

            <Header
                withMidnightCountdown
                titleLoading={isReloadLoading}
                rightActions={renderHeaderActions()}
            />

            <MainContainer>{renderWorkContent()}</MainContainer>

            {modal === "skipPoint" && (
                <SkipPointModal isOpen onClose={handleCloseModal} />
            )}

            {modal === "dayStarted" && (
                <DayStartedModal onClose={handleCloseModal} />
            )}

            <ProgressLoader
                open={queueProgress.isRunning}
                tasks={queueProgress.tasks}
            />

            <TasksModalsContainer
                modal={tasksInfoModal}
                onClose={handleCloseTasksInfoModal}
            />
        </>
    );
};

export default WorkPlan;
